import Config from "../config/config.json";

type BusinessHoursResponse = {
    inBusinessHours: boolean;
};

type CreateSessionResponse = {
    room: any;
    roomId: string;
    token: string;
    guest_jid: string;
    flowData: any;
}

type JoinSessionResponse = {
    ttl: number;
}

type GetSessionResponse = {
    token:string;
    reasonForChat: string;
}

class UniversalLivechatService {
    getToken() {
        return new URLSearchParams(window.location.search).get("token");
    }

    checkAvailability(language="en", reason: string) {
        return new Promise((resolve, reject) => {
            fetch(Config.chatService.url + "/availability?language="+language+"&reason="+reason+"&token="+this.getToken(), {
                method: "get",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                }
            }).then(response => {
                if (response.status === 200) {
                    response.json().then(jsonResponse => {
                        resolve(jsonResponse)
                    }).catch(err => {
                        console.log(err);
                        reject({"data": {"message": "noAgentAvailable"}})
                    });
                } else {
                    reject({"data": {"message": "noAgentAvailable"}})
                }
            }).catch(err => {
                console.log("[UniversalLivechatService] Error trying to find available agents => ", err);
                reject(err);
            })
        });
    }

    createSession(language: string, reasonForChat: string): Promise<CreateSessionResponse> {
        return new Promise((resolve, reject) => {
            fetch(
                Config.chatService.url +
                "/create-session?token=" + this.getToken(),
                {
                    method: "post",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({language: language, reasonForChat: reasonForChat})
                }
            ).then(response => {
                response.json().then(jsonResponse => {
                    console.log(response)
                    console.log(jsonResponse)
                    if (response.status === 200) {
                        resolve(jsonResponse);
                    } else if (response.status === 401 && jsonResponse.type === "token") {
                        console.log("Token error.", response);
                        reject({"data": {"message": "badToken"}});
                    } else {
                        console.log("[UniversalLivechatService] Error creating chat session => ", response);
                        reject("[UniversalLivechatService] Error creating chat session => " + response);
                    }
                }).catch(err => {
                    console.log(err);
                    reject(err);
                });
            }).catch(err => {
                console.log("[UniversalLivechatService] Error creating chat session => ", err);
                reject(err);
            })
        });
    }

    joinSession(bubble: any, guestJid: string, flowData: any, changedReason=false): Promise<JoinSessionResponse> {
        return new Promise((resolve, reject) => {
            fetch(
                Config.chatService.url +
                "/join-session?token=" + this.getToken(),
                {
                    method: "post",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({room: bubble, guest_jid: guestJid, userData: flowData, changedReason: changedReason})
                }
            ).then(response => {
                if (response.status === 200) {
                    response.json().then(jsonResponse => {
                        resolve(jsonResponse);
                    }).catch(err => {
                        console.log("[UniversalLivechatService] Error while joining session : ", err);
                        reject({"data": {"message": "loginFailed"}})
                    });
                } else {
                    console.log("[UniversalLivechatService] Session does not exist. We need to create a new one");
                    reject({"data": {"message": "loginFailed"}})
                }
            }).catch(err => {
                console.log("[UniversalLivechatService] Error while joining session => ", err);
                reject({"data": {"message": "loginFailed"}})
            })
        });
    }

    getSession(room: any): Promise<GetSessionResponse> {
        return new Promise((resolve, reject) => {
            fetch(Config.chatService.url + "/session?roomId=" + room.id + "&token=" + this.getToken(), {
                method: "get",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                }
            }).then(response => {
                if (response.status === 200) {
                    response.json().then(jsonResponse => {
                        resolve(jsonResponse);
                    }).catch(err => {
                        console.log("[UniversalLivechatService] Error while retrieving session : ", err, "We need to create a new one");
                        reject({"data": {"message": "loginFailed"}})
                    });
                } else {
                    console.log("[UniversalLivechatService] Session does not exist. We need to create a new one");
                    reject({"data": {"message": "loginFailed"}})
                }
            }).catch(err => {
                console.log("[UniversalLivechatService] Error while getting session => ", err);
                reject({"data": {"message": "loginFailed"}})
            })
        });
    }

    getBusinessHours(language: string): Promise<BusinessHoursResponse> {
        return new Promise((resolve, reject) => {
            fetch(Config.chatService.url + Config.chatService.businessHours+'?language='+ language + "&token=" + this.getToken(), {
                method: 'get',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            }).then(response => {
                response.json().then(jsonResponse => {
                    response.status === 200 ? resolve(jsonResponse) : reject(jsonResponse);
                });
            }).catch(function(err) {
                console.log("[UniversalLivechatService] Error getting availability => ",err);
                reject(err);
            });
        })
    }

    deleteSession = async (roomId: string, deletionReason = undefined, reasonForChat = undefined, languageRequested = undefined) => {
        fetch(Config.chatService.url + "/delete-session", {
            method: "post",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                roomId: roomId,
                deletionReason: deletionReason,
                reasonForChat: reasonForChat,
                languageRequested: languageRequested
            })
        }).then(() => {
            console.log("[UniversalLivechatService] Session has been deleted");
        }).catch(err => {
            console.log("[UniversalLivechatService] Error trying to delete session => ", err);
        })
    }

    sendInformationMessageToUser = async (roomId: string, reason: string, language: string) => {
        fetch(Config.chatService.url + "/inform-user?token=" + this.getToken(), {
            method: "post",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                roomId: roomId,
                reason: reason,
                language: language
            })
        }).then(() => {
            console.log("[UniversalLivechatService] User has been informed of the reason: ", reason);
        }).catch(() => {
            console.log("[UniversalLivechatService] Error while informing user of the reason: ", reason);
        })
    }
}

export default new UniversalLivechatService();
